import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Box, Inline, Link, useBreakpoint } from "@seneca/senekit";

import { getUserEmail } from "seneca-common/features/user/state";

import useUpdateApp from "features/app/hooks/useUpdateApp";
import { RocketEmoji } from "features/common/emoji/components/Emoji";
import Logo from "features/common/logos/Logo";
import { linkToDashboard } from "features/dashboard/links";
import {
  makeLinkToWhatIsPremium,
  SUBSCRIPTION_JOURNEY_SOURCES
} from "features/dashboard/pages/what-is-premium-page/routes/links";
import { useIsPlurallApp } from "features/plurall";
import {
  getSellPremiumToUser,
  getSellUpgradeToUser
} from "features/subscriptions/utils/showPremiumAds";
import {
  Stage,
  useTutoringActivationJourneyLink
} from "features/tutoring/activation-journey";
import { useOpenTutoringActivationJourneyIfInactive } from "features/tutoring/useOpenTutoringActivationJourneyIfInactive";

import { getLinkToPlurallStore } from "../../../../plurall/links";
import DesktopNavbar from "./DesktopNavbar";
import MobileNavbar from "./MobileNavbar";
import TabletNavbar from "./TabletNavbar";

type Props = {
  name: string;
  isTeacher: boolean;
  showRegionLink: boolean;
  hasLiveSubscription: boolean;
  signOut: () => void;
  locale: string;
  region: string;
  children: ReactNode;
};

export type NavbarProps = Omit<Props, "children"> & {
  email?: string | null;
  sellUpgradeToUser: boolean;
  sellPremiumToUser: boolean;
  updateAppVersion: () => void;
  appVersionUpdateReady: boolean;
  appVersionIsUpdating: boolean;
  hasTutoringInactive: boolean;
  isPlurallApp: boolean;
  linkToWhatIsPremium: string;
  linkToTutoringActivation: ReturnType<typeof useLocation>;
  GetPremium: () => ReactNode;
  UpgradePremium: () => ReactNode;
  ActivateTutoring: () => ReactNode;
};

export default function DashboardNavbar({ children, ...props }: Props) {
  const { isMobileOnly, isTabletOnly } = useBreakpoint();

  const sellUpgradeToUser = useSelector(getSellUpgradeToUser);
  const sellPremiumToUser = useSelector(getSellPremiumToUser);
  const email = useSelector(getUserEmail);

  const { update, ready, updating } = useUpdateApp();

  const { hasTutoringInactive } = useOpenTutoringActivationJourneyIfInactive();

  const isPlurallApp = useIsPlurallApp();
  const linkToWhatIsPremium = isPlurallApp
    ? getLinkToPlurallStore()
    : linkToWhatIsPremiumPage();

  const linkToTutoringActivation = useTutoringActivationJourneyLink({
    stage: Stage.Start
  });

  const commonProps: NavbarProps = {
    ...props,
    email,
    sellUpgradeToUser,
    sellPremiumToUser,
    updateAppVersion: update,
    appVersionUpdateReady: ready,
    appVersionIsUpdating: updating,
    hasTutoringInactive,
    isPlurallApp,
    linkToWhatIsPremium,
    linkToTutoringActivation,
    GetPremium,
    UpgradePremium,
    ActivateTutoring
  };

  if (isMobileOnly) {
    return (
      <MobileNavbar {...commonProps}>
        <NavbarWrapper />
        {children}
      </MobileNavbar>
    );
  }

  if (isTabletOnly) {
    return (
      <>
        <NavbarWrapper>
          <TabletNavbar {...commonProps} />
        </NavbarWrapper>
        {children}
      </>
    );
  }

  return (
    <>
      <NavbarWrapper>
        <DesktopNavbar {...commonProps} />
      </NavbarWrapper>
      {children}
    </>
  );
}

function NavbarWrapper({ children }: { children?: ReactNode }) {
  return (
    <Box
      marginX={["large", "xlarge"]}
      marginY="xlarge"
      dataTest="DashboardNavbar"
    >
      <Inline
        flexWrap="nowrap"
        justifyContent="space-between"
        alignItems="center"
      >
        <Link to={linkToDashboard()}>
          <Logo height={["36px", "40px"]} maxWidth="200px" />
        </Link>

        {children}
      </Inline>
    </Box>
  );
}

const GetPremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Get premium
  </Inline>
);

const UpgradePremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Upgrade
  </Inline>
);

const ActivateTutoring = () => (
  <Inline space="medium">
    <RocketEmoji />
    Activate tutoring
  </Inline>
);

function linkToWhatIsPremiumPage() {
  return makeLinkToWhatIsPremium({
    source: SUBSCRIPTION_JOURNEY_SOURCES.DASHBOARD_NAVBAR
  });
}
